/**
 * Настройки → Люди (docs/07-tasks-notion.md).
 *
 * Токен интеграции не имеет права перечислять пользователей Notion, зато
 * имена и почты приходят внутри самих задач. Поэтому список участников
 * собирается из встреченных в задачах, а сопоставление подтверждается здесь.
 */
import { Badge, Card, Select } from '@/ds';
import { EmptyState } from '@/components/EmptyState';
import { Provenance } from '@/components/money';
import { useOperation, usePeople } from '@/api/hooks';
import { formatRelative } from '@/lib/format';
import { useControlSize } from '@/lib/useControlSize';
import { QueryState } from '@/components/QueryState';

export function PeopleTab() {
  const size = useControlSize();
  const people = usePeople();
  const link = useOperation<{ userId: string; notionUserId: string | null }>('people.link');

  const users = people.data?.users ?? [];
  const notionPeople = people.data?.notionPeople ?? [];
  const unmatched = notionPeople.filter((person) => !person.isLinked);

  if (people.isLoading || people.isError) {
    return <QueryState query={people} what="участников" />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <p
        style={{
          font: '400 13px/1.5 var(--font-text)',
          color: 'var(--text-muted)',
          maxWidth: 620,
        }}
      >
        Связь нужна, чтобы показывать, кто чем занят, и отправлять уведомления по задачам.
        Совпадение по рабочей почте приложение находит само, остальное подтверждается здесь.
        Несопоставленный участник виден в задачах своим именем из Notion, но уведомления ему
        не уходят: их некуда слать.
      </p>

      <Card padding="md">
        <div
          style={{
            font: '500 16px var(--font-display)',
            letterSpacing: 'var(--ls-heading)',
            color: 'var(--text-strong)',
            marginBottom: 'var(--space-5)',
          }}
        >
          Пользователи приложения
        </div>

        <div className="kj-scroll-x">
          <table className="kj-table">
            <thead>
              <tr>
                <th>Имя</th>
                <th>Почта</th>
                <th>Участник Notion</th>
                <th>Telegram</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td data-label="Имя" style={{ color: 'var(--text-strong)', fontWeight: 500 }}>{user.name}</td>
                  <td data-label="Почта" className="kj-mono">{user.email}</td>
                  <td data-label="Участник Notion" style={{ minWidth: 220 }}>
                    <Select
                      size={size}
                      options={[
                        { value: '', label: 'Не сопоставлен' },
                        ...notionPeople.map((person) => ({
                          value: person.notionUserId,
                          label: person.name + (person.email ? ` · ${person.email}` : ''),
                        })),
                      ]}
                      value={user.notionUserId ?? ''}
                      onChange={(e) =>
                        link.mutate({ userId: user.id, notionUserId: e.target.value || null })
                      }
                    />
                  </td>
                  <td data-label="Telegram" style={{ color: 'var(--text-subtle)' }}>
                    {user.telegramLinked ? 'привязан' : 'не привязан'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card padding="md">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-4)',
            flexWrap: 'wrap',
            marginBottom: 'var(--space-5)',
          }}
        >
          <div
            style={{
              font: '500 16px var(--font-display)',
              letterSpacing: 'var(--ls-heading)',
              color: 'var(--text-strong)',
            }}
          >
            Не сопоставлены
          </div>
          {unmatched.length === 0 ? <Badge tone="success" dot>Все связаны</Badge> : null}
        </div>

        {notionPeople.length === 0 ? (
          <EmptyState
            icon="Users"
            title="Участники ещё не встречались"
            description="Список собирается из задач: как только в Notion появится задача с ответственным, он окажется здесь."
          />
        ) : unmatched.length === 0 ? (
          <Provenance>
            Каждый участник Notion, встреченный в задачах, связан с пользователем приложения.
          </Provenance>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {unmatched.map((person) => (
              <div
                key={person.notionUserId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-4)',
                  flexWrap: 'wrap',
                  paddingBottom: 'var(--space-4)',
                  borderBottom: '1px solid var(--border-subtle)',
                }}
              >
                <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                  <div style={{ font: '500 14px var(--font-text)', color: 'var(--text-strong)' }}>
                    {person.name}
                  </div>
                  <Provenance>
                    {(person.email ?? 'почта не указана') +
                      ` · встречался ${formatRelative(person.lastSeenAt)}`}
                  </Provenance>
                </div>
                <div style={{ minWidth: 220 }}>
                  <Select
                    size={size}
                    options={[
                      { value: '', label: 'Привязать к пользователю' },
                      ...users.map((user) => ({ value: user.id, label: user.name })),
                    ]}
                    value=""
                    onChange={(e) => {
                      if (!e.target.value) return;
                      link.mutate({ userId: e.target.value, notionUserId: person.notionUserId });
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
